/* ============================================================
   SAM-198 (mock 7b) — "New business" form: pure state + payload rules,
   kept out of NewBusinessPage.tsx so they are unit-testable.

   The payload is the same subset of `business` columns the old create
   path (BusinessDetailPanel in 'create' mode) sent through
   `useCreateBusiness` (POST /crud/business) — no new fields, no new
   endpoint. Schedule, team and services are set up on the business page
   after creating it.
   ============================================================ */
import type { Business } from '../../hooks/useBusinessCrud';
import { NEW_BUSINESS_LOCATION, locationCreateColumns, locationIssues, type LocationForm } from './location/locationModel';

export interface NewBusinessForm {
  name: string;
  logo_url: string;
  email: string;
  phone_number: string;
  business_category_id: number | null;
  business_type_id: number | null;
  /** SAM-1237: where it is (Cap Cana, external or virtual). */
  location: LocationForm;
}

export const EMPTY_NEW_BUSINESS: NewBusinessForm = {
  name: '',
  logo_url: '',
  email: '',
  phone_number: '',
  business_category_id: null,
  business_type_id: null,
  location: NEW_BUSINESS_LOCATION,
};

export type NewBusinessIssue = 'name' | 'email' | 'subcategory' | 'location';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** What still blocks "Create business". A category without a subcategory is incomplete
 * classification (SAM-163, same rule as the business Profile). */
export function newBusinessIssues(form: NewBusinessForm): NewBusinessIssue[] {
  const issues: NewBusinessIssue[] = [];
  if (!form.name.trim()) issues.push('name');
  if (form.email.trim() && !EMAIL_RE.test(form.email.trim())) issues.push('email');
  if (form.business_category_id != null && form.business_type_id == null) issues.push('subcategory');
  if (locationIssues(form.location).length) issues.push('location');
  return issues;
}

/** True once the admin typed or picked anything — Cancel then asks before discarding. */
export function isNewBusinessDirty(form: NewBusinessForm): boolean {
  return JSON.stringify(form) !== JSON.stringify(EMPTY_NEW_BUSINESS);
}

/** A phone value that is only a dial code ("+1") is treated as empty. */
function cleanPhone(phone: string): string {
  const trimmed = phone.trim();
  return /^\+?\d{1,4}$/.test(trimmed) ? '' : trimmed;
}

/** POST body. A new business is born inactive and hidden (not in the app, not at the front desk); the admin turns it on in Settings. */
export function newBusinessPayload(form: NewBusinessForm): Partial<Business> {
  const phone = cleanPhone(form.phone_number);
  return {
    name: form.name.trim(),
    business_category_id: form.business_category_id as number,
    business_type_id: form.business_type_id as number,
    ...(form.email.trim() ? { email: form.email.trim() } : {}),
    ...(phone ? { phone_number: phone } : {}),
    ...(form.logo_url ? { logo_url: form.logo_url } : {}),
    ...locationCreateColumns(form.location),
    is_active: false,
    show_in_app: false,
    show_gatekeeper: false,
  } as Partial<Business>;
}

/**
 * The new row's id from the crud POST response. httpClient hands back the body (`{data: [{id}]}`), so that is
 * the real shape (SAM-1311); the axios-wrapped and single-object forms are still read.
 */
export function createdBusinessId(response: unknown): string | null {
  const idOf = (node: unknown): unknown => {
    const row = Array.isArray(node) ? node[0] : node;
    return row && typeof row === 'object' ? (row as { id?: unknown }).id : undefined;
  };
  const top = response as { id?: unknown; data?: unknown } | null | undefined;
  const inner = top?.data as { id?: unknown; data?: unknown } | unknown[] | null | undefined;
  const id =
    idOf(inner) ??
    (inner && !Array.isArray(inner) ? idOf((inner as { data?: unknown }).data) : undefined) ??
    top?.id;
  return id == null || id === '' ? null : String(id);
}
